import React from 'react';
import moment from 'moment';
import { useLocalStorageState, enumValue } from '~/util';

const ClientContext = React.createContext();
const currentDate = moment();

function ClientProvider(props) {
    const [client, setClient] = React.useState(window.app.client ? window.app.client : {});
    const { item: reportYear, setItem: setReportYear } = useLocalStorageState(
        'reportYear',
        currentDate.year()
    );
    const isReportYearAvailable = React.useMemo(() => {
        if (!client?.report_year_settings) return false;
        let available = false;
        Object.values(client.report_year_settings).forEach((val) => {
            if (val === null) return;
            const date = moment(val);
            if (date.isSameOrAfter(currentDate, 'day') && date.year() === currentDate.year())
                available = true;
        });
        return available;
    }, [client]);
    const reportYearDates = React.useMemo(() => {
        if (!client?.report_year_settings) return {};
        const dates = {};
        Object.entries(client.report_year_settings).forEach(([key, val]) => {
            if (val === null) return;
            const date = new Date(val.replace(/-/g, '/'));
            if (date instanceof Date && !Number.isNaN(date.getTime())) {
                dates[key] = date;
            }
        });
        return dates;
    }, [client]);
    const isPriorYearSelected = React.useMemo(() => {
        return reportYear < currentDate.year();
    }, [reportYear]);
    const formattedReportYear = React.useMemo(() => {
        return Number(reportYear);
    }, [reportYear]);

    // Ensure that selection does not get locked to prior year if toggle is not available
    React.useEffect(() => {
        if (!isReportYearAvailable && isPriorYearSelected) {
            setReportYear(currentDate.year());
        }
    }, [client, isPriorYearSelected, isReportYearAvailable, setReportYear]);

    function isMarketValidEmailDomain(email) {
        if (
            !client?.settings?.access_type_domain_restrictions[
                enumValue(['user', 'access_type', 'Market'])
            ]
        )
            return false;
        return client?.settings?.access_type_domain_restrictions[
            enumValue(['user', 'access_type', 'Market'])
        ].some((match) => email.toLowerCase().endsWith(match));
    }

    function availableUserAccessTypes(email) {
        return Object.keys(window.app.enums.user.access_type)
            .map((x) => ({
                value: x,
                label: window.app.enums.user.access_type[x],
            }))
            .filter((x) => x.label !== 'Market' || isMarketValidEmailDomain(email));
    }

    const methods = {
        client,
        setClient,
        reportYear: formattedReportYear,
        setReportYear,
        isReportYearAvailable,
        reportYearDates,
        isPriorYearSelected,
        isMarketValidEmailDomain,
        availableUserAccessTypes,
    };

    return <ClientContext.Provider value={methods} {...props} />;
}

export { ClientContext, ClientProvider };
